<template>
  <div class="dashboard-page">
    <header class="dashboard-page__header">
      <div>
        <p class="dashboard-page__eyebrow">Painel administrativo</p>
        <h1 class="dashboard-page__title">Dashboard</h1>
        <p class="dashboard-page__subtitle">
          Resumo financeiro do período e indicadores operacionais em tempo real.
        </p>
      </div>

      <div class="dashboard-page__filters">
        <div class="dashboard-page__presets">
          <v-btn
            v-for="preset in periodPresets"
            :key="preset.id"
            small
            depressed
            :color="activePreset === preset.id ? 'primary' : undefined"
            :outlined="activePreset !== preset.id"
            @click="applyPreset(preset.id)"
          >
            {{ preset.label }}
          </v-btn>
        </div>

        <v-dialog ref="dialog" v-model="dialog" persistent width="320px">
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="handleFormatDate"
              class="dashboard-page__period"
              label="Período personalizado"
              prepend-inner-icon="mdi-calendar-month-outline"
              outlined
              dense
              readonly
              hide-details
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="selectedRange" range locale="pt-br">
            <v-spacer />
            <v-btn text color="grey darken-1" @click="closeDialog">Cancelar</v-btn>
            <v-btn color="primary" text @click="handleLoadDashboard('custom')">Aplicar</v-btn>
          </v-date-picker>
        </v-dialog>
      </div>
    </header>

    <v-skeleton-loader
      v-if="loading"
      type="image, image, image"
      class="dashboard-page__skeleton"
      boilerplate
    />

    <div v-else class="dashboard-page__content">
      <section class="dashboard-section dashboard-section--hero">
        <div class="dashboard-section__head">
          <div>
            <h2 class="dashboard-section__title">Resultado do período</h2>
            <p class="dashboard-section__description">{{ periodLabel }}</p>
          </div>
          <v-chip small color="primary" text-color="white" label>
            <v-icon left small>mdi-calendar-range</v-icon>
            Filtrado por data
          </v-chip>
        </div>

        <v-row dense>
          <v-col cols="12" md="6">
            <card-resume-dashboard-vue
              tone="highlight"
              icon="mdi-cash-multiple"
              title1="Faturamento total"
              subtitle="Pagamentos confirmados no período"
              :title2="formatPrice(dashboard.invoicing)"
            />
          </v-col>
          <v-col cols="12" md="6">
            <card-resume-dashboard-vue
              tone="highlight"
              icon="mdi-trending-up"
              title1="Lucro operacional"
              subtitle="Faturamento menos repasse, fases, cashback direto e BRBonus"
              :title2="formatPrice(dashboard.profit)"
            />
          </v-col>
        </v-row>
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section__head">
          <div>
            <h2 class="dashboard-section__title">Adesões e cashback</h2>
            <p class="dashboard-section__description">
              Movimentação de parceiros premium e créditos pagos aos consumidores.
            </p>
          </div>
        </div>

        <v-row dense>
          <v-col
            v-for="metric in getAdhesionAndCashbackMetrics()"
            :key="metric.title"
            cols="12"
            sm="6"
            lg="3"
          >
            <card-resume-dashboard-vue
              compact
              :tone="metric.tone"
              :icon="metric.icon"
              :title1="metric.title"
              :subtitle="metric.subtitle"
              :title2="metric.value"
            />
          </v-col>
        </v-row>
      </section>

      <v-row dense class="dashboard-page__split">
        <v-col cols="12" lg="6">
          <section class="dashboard-section dashboard-section--panel">
            <div class="dashboard-section__head">
              <div>
                <h2 class="dashboard-section__title">Base cadastral</h2>
                <p class="dashboard-section__description">
                  Totais acumulados da plataforma, com novos cadastros no período.
                </p>
              </div>
              <v-chip small outlined label>Acumulado</v-chip>
            </div>

            <div class="dashboard-list">
              <div
                v-for="metric in getRegistryMetrics()"
                :key="metric.title"
                class="dashboard-list__item"
              >
                <div class="dashboard-list__icon">
                  <v-icon size="20" color="inherit">{{ metric.icon }}</v-icon>
                </div>
                <div class="dashboard-list__content">
                  <span class="dashboard-list__label">{{ metric.title }}</span>
                  <span class="dashboard-list__hint">{{ metric.subtitle }}</span>
                </div>
                <strong class="dashboard-list__value">{{ metric.value }}</strong>
              </div>
            </div>
          </section>
        </v-col>

        <v-col cols="12" lg="6">
          <section class="dashboard-section dashboard-section--panel">
            <div class="dashboard-section__head">
              <div>
                <h2 class="dashboard-section__title">Fila operacional</h2>
                <p class="dashboard-section__description">
                  Itens que exigem ação da equipe, independente do período.
                </p>
              </div>
              <v-chip
                small
                :color="getOperationalAlertsCount() > 0 ? 'error' : 'success'"
                text-color="white"
                label
              >
                {{ getOperationalAlertsCount() }} pendência(s)
              </v-chip>
            </div>

            <div class="dashboard-list">
              <div
                v-for="metric in getOperationalMetrics()"
                :key="metric.title"
                class="dashboard-list__item"
                :class="{ 'dashboard-list__item--alert': metric.isAlert }"
              >
                <div class="dashboard-list__icon" :class="`dashboard-list__icon--${metric.tone}`">
                  <v-icon size="20" color="inherit">{{ metric.icon }}</v-icon>
                </div>
                <div class="dashboard-list__content">
                  <span class="dashboard-list__label">{{ metric.title }}</span>
                  <span class="dashboard-list__hint">{{ metric.subtitle }}</span>
                </div>
                <strong class="dashboard-list__value">{{ metric.value }}</strong>
              </div>
            </div>
          </section>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import CardResumeDashboardVue from "@/components/cardResumeDashboard/CardResumeDashboard.vue";
import { GetDashboard } from "@/services/dashboard/GetDashboard";
import { GetAllFranchises } from "@/services/franchises/GetAllFranchises";
import { GetAllOnbordings } from "@/services/onboardings/GetAllOnboardings";
import { GetAllPartners } from "@/services/partners/GetAllPartners";
import { GetListWithdrawal } from "@/services/withdrawal/GetListWithdrawal";
import { DashboardType } from "@/types/dashboard/Dashboard";

type PeriodPresetId = "7d" | "30d" | "90d" | "custom";

interface PeriodPreset {
  id: PeriodPresetId;
  label: string;
  days?: number;
}

type MetricTone = "primary" | "success" | "warning" | "danger" | "neutral";

interface OperationalSummary {
  totalFranchises: number;
  waitingOnboardings: number;
  pendingPartners: number;
  pendingWithdrawals: number;
}

interface HomeViewState {
  dialog: boolean;
  activePreset: PeriodPresetId;
  selectedRange: string[];
  startDate: string;
  endDate: string;
  loading: boolean;
  dashboard: DashboardType;
  operationalSummary: OperationalSummary;
  periodPresets: PeriodPreset[];
}

function formatDashboardPrice(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value || 0);
}

function formatDashboardCount(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value || 0);
}

export default defineComponent({
  components: { CardResumeDashboardVue },
  data(): HomeViewState {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const formattedToday = today.toISOString().split("T")[0];
    const formattedThirtyDaysAgo = thirtyDaysAgo.toISOString().split("T")[0];

    return {
      dialog: false,
      activePreset: "30d" as PeriodPresetId,
      selectedRange: [formattedThirtyDaysAgo, formattedToday],
      startDate: formattedThirtyDaysAgo,
      endDate: formattedToday,
      loading: true,
      dashboard: {
        brpointsPerPeriod: 0,
        currentBrpointValue: 0,
        customersPerPeriod: 0,
        invoicing: 0,
        partnersPerPeriod: 0,
        profit: 0,
        totalCustomers: 0,
        totalPartners: 0,
        partnerAdhesionInvoicing: 0,
        partnerAdhesionProfit: 0,
        dailyCashbackPaid: 0,
        directCashbackPaid: 0,
      } as DashboardType,
      operationalSummary: {
        totalFranchises: 0,
        waitingOnboardings: 0,
        pendingPartners: 0,
        pendingWithdrawals: 0,
      },
      periodPresets: [
        { id: "7d", label: "7 dias", days: 7 },
        { id: "30d", label: "30 dias", days: 30 },
        { id: "90d", label: "90 dias", days: 90 },
      ] as PeriodPreset[],
    };
  },
  computed: {
    periodLabel(): string {
      return `De ${this.handleFormatDate}`;
    },
    handleFormatDate(): string {
      const datesString = this.selectedRange?.map((dates: string | undefined) => {
        if (dates) {
          const [year, month, day] = dates.split("-");
          return `${day}/${month}/${year}`;
        }
        return "";
      });
      return (datesString || []).filter(Boolean).join(" - ");
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    applyPreset(presetId: PeriodPresetId) {
      const preset = this.periodPresets.find((item) => item.id === presetId);
      if (!preset?.days) return;

      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - preset.days);

      this.selectedRange = [start.toISOString().split("T")[0], end.toISOString().split("T")[0]];
      this.handleLoadDashboard(presetId);
    },
    async handleLoadDashboard(preset?: PeriodPresetId) {
      this.loading = true;
      this.activePreset = preset || this.activePreset;

      if (this.selectedRange.length > 0) {
        if (this.selectedRange[0] > this.selectedRange[1]) {
          this.startDate = this.selectedRange[1];
          this.endDate = this.selectedRange[0];
        } else {
          this.startDate = this.selectedRange[0];
          this.endDate = this.selectedRange[1];
        }
      }

      const results = await Promise.allSettled([
        new GetDashboard().execute(this.startDate, this.endDate),
        new GetAllFranchises().execute(),
        new GetAllOnbordings().execute("WAITING"),
        new GetAllPartners().execute("PENDING"),
        new GetListWithdrawal().execute(),
      ]);

      const [
        dashboardResult,
        franchisesResult,
        onboardingsResult,
        partnersResult,
        withdrawalsResult,
      ] = results;

      if (dashboardResult.status === "fulfilled") {
        this.dashboard = dashboardResult.value;
      }

      if (franchisesResult.status === "fulfilled") {
        this.operationalSummary.totalFranchises = franchisesResult.value.length;
      }

      if (onboardingsResult.status === "fulfilled") {
        this.operationalSummary.waitingOnboardings = onboardingsResult.value.length;
      }

      if (partnersResult.status === "fulfilled") {
        this.operationalSummary.pendingPartners = partnersResult.value.length;
      }

      if (withdrawalsResult.status === "fulfilled") {
        this.operationalSummary.pendingWithdrawals = withdrawalsResult.value.filter((item) =>
          this.isPendingWithdrawal(item.status)
        ).length;
      }

      if (results.some((result) => result.status === "rejected")) {
        this.$store.dispatch("showSnack", {
          message: "Alguns indicadores do dashboard não puderam ser atualizados agora.",
          color: "warning",
          timer: 4000,
        });
      }

      this.loading = false;
      this.closeDialog();
    },
    isPendingWithdrawal(status?: string | String): boolean {
      const normalizedStatus = String(status || "");
      return !normalizedStatus || normalizedStatus === "PENDING" || normalizedStatus === "WAITING";
    },
    getAdhesionAndCashbackMetrics() {
      return [
        {
          title: "Faturamento de adesões",
          subtitle: "Parceiros premium no período",
          value: formatDashboardPrice(this.dashboard.partnerAdhesionInvoicing),
          icon: "mdi-handshake-outline",
          tone: "primary" as MetricTone,
        },
        {
          title: "Lucro de adesões",
          subtitle: "Margem BRPoints em adesões",
          value: formatDashboardPrice(this.dashboard.partnerAdhesionProfit),
          icon: "mdi-chart-line",
          tone: "success" as MetricTone,
        },
        {
          title: "Cashback diário pago",
          subtitle: "Créditos de fases e BRBonus",
          value: formatDashboardPrice(this.dashboard.dailyCashbackPaid),
          icon: "mdi-calendar-clock",
          tone: "warning" as MetricTone,
        },
        {
          title: "Cashback direto pago",
          subtitle: "Cashback instantâneo nas compras",
          value: formatDashboardPrice(this.dashboard.directCashbackPaid),
          icon: "mdi-lightning-bolt-outline",
          tone: "neutral" as MetricTone,
        },
      ];
    },
    getRegistryMetrics() {
      return [
        {
          title: "Consumidores cadastrados",
          subtitle: `${formatDashboardCount(this.dashboard.customersPerPeriod)} novos no período`,
          value: formatDashboardCount(this.dashboard.totalCustomers),
          icon: "mdi-account-group-outline",
        },
        {
          title: "Parceiros cadastrados",
          subtitle: `${formatDashboardCount(this.dashboard.partnersPerPeriod)} novos no período`,
          value: formatDashboardCount(this.dashboard.totalPartners),
          icon: "mdi-store-outline",
        },
      ];
    },
    getOperationalMetrics() {
      return [
        {
          title: "Franquias cadastradas",
          subtitle: "Total ativo na rede",
          value: formatDashboardCount(this.operationalSummary.totalFranchises),
          icon: "mdi-domain",
          tone: "neutral",
          isAlert: false,
        },
        {
          title: "Onboardings aguardando",
          subtitle: "Consumidores em análise",
          value: formatDashboardCount(this.operationalSummary.waitingOnboardings),
          icon: "mdi-account-clock-outline",
          tone: "warning",
          isAlert: this.operationalSummary.waitingOnboardings > 0,
        },
        {
          title: "Parceiros pendentes",
          subtitle: "Cadastros aguardando aprovação",
          value: formatDashboardCount(this.operationalSummary.pendingPartners),
          icon: "mdi-store-alert-outline",
          tone: "warning",
          isAlert: this.operationalSummary.pendingPartners > 0,
        },
        {
          title: "Saques aguardando",
          subtitle: "Solicitações em aberto",
          value: formatDashboardCount(this.operationalSummary.pendingWithdrawals),
          icon: "mdi-cash-remove",
          tone: "danger",
          isAlert: this.operationalSummary.pendingWithdrawals > 0,
        },
      ];
    },
    getOperationalAlertsCount(): number {
      return this.getOperationalMetrics().filter((item) => item.isAlert).length;
    },
    formatPrice(value: number): string {
      return formatDashboardPrice(value);
    },
    formatCount(value: number): string {
      return formatDashboardCount(value);
    },
  },
  mounted() {
    this.handleLoadDashboard("30d");
  },
});
</script>

<style scoped lang="scss">
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.dashboard-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: var(--shadow-sm);
}

.dashboard-page__eyebrow {
  margin: 0 0 6px;
  color: var(--color-primary);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dashboard-page__title {
  margin: 0;
  color: var(--color-text);
  font-size: clamp(1.7rem, 2.5vw, 2.2rem);
  font-weight: 700;
  line-height: 1.1;
}

.dashboard-page__subtitle {
  margin: 8px 0 0;
  max-width: 560px;
  color: var(--color-text-muted);
  font-size: 0.95rem;
  line-height: 1.5;
}

.dashboard-page__filters {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  min-width: 280px;
}

.dashboard-page__presets {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.dashboard-page__period {
  width: 280px;
}

.dashboard-page__content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dashboard-page__split {
  margin-top: 0;
}

.dashboard-section {
  padding: 22px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.dashboard-section--hero {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.dashboard-section--panel {
  height: 100%;
}

.dashboard-section__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.dashboard-section__title {
  margin: 0;
  color: var(--color-text);
  font-size: 1.1rem;
  font-weight: 700;
}

.dashboard-section__description {
  margin: 6px 0 0;
  color: var(--color-text-muted);
  font-size: 0.88rem;
  line-height: 1.45;
}

.dashboard-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dashboard-list__item {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-muted);
}

.dashboard-list__item--alert {
  border-color: rgba(225, 29, 72, 0.18);
  background: rgba(225, 29, 72, 0.04);
}

.dashboard-list__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(100, 116, 139, 0.12);
  color: #64748b;
}

.dashboard-list__icon--warning {
  background: rgba(217, 119, 6, 0.12);
  color: #d97706;
}

.dashboard-list__icon--danger {
  background: rgba(225, 29, 72, 0.12);
  color: #e11d48;
}

.dashboard-list__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dashboard-list__label {
  color: var(--color-text);
  font-size: 0.92rem;
  font-weight: 600;
}

.dashboard-list__hint {
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.dashboard-list__value {
  color: var(--color-text);
  font-size: 1.2rem;
  font-weight: 700;
  white-space: nowrap;
}

.dashboard-page__skeleton {
  border-radius: var(--radius-md);
}

@media (max-width: 960px) {
  .dashboard-page__header {
    flex-direction: column;
  }

  .dashboard-page__filters {
    align-items: stretch;
    width: 100%;
    min-width: 0;
  }

  .dashboard-page__presets {
    justify-content: flex-start;
  }

  .dashboard-page__period {
    width: 100%;
  }

  .dashboard-section__head {
    flex-direction: column;
  }
}
</style>
